import { canReadAllProposals, isInternalOperationalStaff, PROPOSALS_READ_ALL_ROLES } from "./proposalOperationalAccess";
import { canManageCustomerSites } from "./customerSiteGovernance";
import { canScheduleServiceVisit } from "./serviceVisitCustomerScheduling";
import { hasRoleCapability, normalizeUserRole, type UserRoleFields } from "./userRoles";

export type StaffCustomerActor = {
  id: number;
  role: string;
  secondaryRolesJson?: unknown;
};

export type StaffCustomerOwner = {
  salespersonId?: number | null;
  salespersonName?: string | null;
};

export type StaffCustomerSection =
  | "identity"
  | "contacts"
  | "sites"
  | "serviceHistory"
  | "commercialSalesWork"
  | "finance";

export type StaffCustomerAccess = {
  canReadCustomer: boolean;
  canManageCustomer: boolean;
  canReadServiceHistory: boolean;
  canReadCommercialSalesWork: boolean;
  canReadFinance: boolean;
  canMutateServiceSites: boolean;
  canScheduleServiceVisit: boolean;
  isReadOnlyBecauseOtherPic: boolean;
  assignedPicName: string | null;
  permittedOperationalSections: StaffCustomerSection[];
  permittedCommercialSections: StaffCustomerSection[];
};

export type CustomerCardTab =
  | "timeline"
  | "purchase"
  | "billing"
  | "projects"
  | "delivered"
  | "proposals"
  | "svr"
  | "media"
  | "ro";

function primaryStaffRole(actor: StaffCustomerActor): string {
  return normalizeUserRole(actor.role);
}

export function canReadStaffCustomerDirectory(
  roleOrUser: string | UserRoleFields | null | undefined,
): boolean {
  if (roleOrUser && typeof roleOrUser === "object") {
    return hasRoleCapability(
      {
        role: normalizeUserRole(roleOrUser.role),
        secondaryRolesJson: roleOrUser.secondaryRolesJson,
      },
      PROPOSALS_READ_ALL_ROLES,
    );
  }
  return isInternalOperationalStaff(roleOrUser) || canReadAllProposals(roleOrUser);
}

export function canReadCustomerRecord(actor: StaffCustomerActor): boolean {
  return canReadStaffCustomerDirectory(actor);
}

export function canManageCustomerRecord(actor: StaffCustomerActor, customer: StaffCustomerOwner): boolean {
  const role = primaryStaffRole(actor);
  if (role === "admin") return true;
  if (role === "sales") {
    return customer.salespersonId != null && customer.salespersonId === actor.id;
  }
  return false;
}

/**
 * Customer-card commercial surfaces: Timeline, Projects, Proposals, sales metrics.
 * Anyone who can open the staff customer card may read these — including programming,
 * installer, and inventory — so mis-tagged sales-adjacent staff (e.g. Jeslin) see the
 * same tabs an admin (e.g. June) sees. Identity mutations stay PIC/admin-gated.
 * Secondary roles participate; primary role is trim+lowercase normalised.
 */
export function canReadCommercialSalesWork(actor: StaffCustomerActor, _customer: StaffCustomerOwner): boolean {
  return canReadCustomerRecord(actor);
}

/**
 * Customer-card finance surfaces: Billing, Purchase History, invoices.
 * Same read set as commercial tabs. Destructive finance mutations (SAPO / Xero /
 * mark-paid) stay admin/finance in those workflows.
 */
export function canReadFinanceCustomerData(actor: StaffCustomerActor): boolean {
  return canReadCustomerRecord(actor);
}

export function resolveStaffCustomerAccess(
  actor: StaffCustomerActor,
  customer: StaffCustomerOwner,
): StaffCustomerAccess {
  const canReadCustomer = canReadCustomerRecord(actor);
  const canManageCustomer = canReadCustomer && canManageCustomerRecord(actor, customer);
  const canReadService = canReadCustomer && canScheduleServiceVisit(primaryStaffRole(actor));
  const commercial = canReadCustomer && canReadCommercialSalesWork(actor, customer);
  const finance = canReadCustomer && canReadFinanceCustomerData(actor);
  const canMutateServiceSites = canReadCustomer && canManageCustomerSites(actor, customer);
  const otherPic =
    primaryStaffRole(actor) === "sales" &&
    customer.salespersonId != null &&
    customer.salespersonId !== actor.id;
  const assignedPicName = customer.salespersonName?.trim() || (otherPic ? "another salesperson" : null);

  const permittedOperationalSections: StaffCustomerSection[] = [];
  if (canReadCustomer) {
    permittedOperationalSections.push("identity", "contacts", "sites");
    if (canReadService) permittedOperationalSections.push("serviceHistory");
  }
  const permittedCommercialSections: StaffCustomerSection[] = [];
  if (commercial) permittedCommercialSections.push("commercialSalesWork");
  if (finance) permittedCommercialSections.push("finance");

  return {
    canReadCustomer,
    canManageCustomer,
    canReadServiceHistory: canReadService,
    canReadCommercialSalesWork: commercial,
    canReadFinance: finance,
    canMutateServiceSites,
    canScheduleServiceVisit: canReadCustomer && canScheduleServiceVisit(primaryStaffRole(actor)),
    isReadOnlyBecauseOtherPic: Boolean(canReadCustomer && otherPic),
    assignedPicName,
    permittedOperationalSections,
    permittedCommercialSections,
  };
}

/**
 * Visible customer-card tabs from access flags only — never portal mode (Sales vs
 * Service) and never a second PIC gate. Order matches CustomerDetail.
 */
export function customerCardVisibleTabs(access: StaffCustomerAccess | null | undefined): CustomerCardTab[] {
  const tabs: CustomerCardTab[] = [];
  if (access?.canReadCommercialSalesWork) tabs.push("timeline");
  if (access?.canReadFinance) tabs.push("purchase", "billing");
  if (access?.canReadCommercialSalesWork) tabs.push("projects");
  tabs.push("delivered");
  if (access?.canReadCommercialSalesWork) tabs.push("proposals");
  tabs.push("svr", "media", "ro");
  return tabs;
}

export function staffCustomerReadOnlyBanner(access: StaffCustomerAccess): string | null {
  if (!access.isReadOnlyBecauseOtherPic) return null;
  const name = access.assignedPicName || "another salesperson";
  return `This customer is managed by ${name}. You have read-only access to this customer record.`;
}
